/**
 * capital_skeleton.ts — a capital's ROAD SKELETON as a CityPlan (capital wave
 * phase 2, 2026-09-15): three ring roads in the radial corridors, six
 * boulevards through the ring gaps to gates at the wall radius (+ a 60 m exit
 * past it), six service stubs to the other ring-3 gaps. Pure and cheap
 * (~1k edges, no CityGenerator run): every plan consumer — stamping, road
 * meshes, furniture, the worn-track mask, footprints — reads it unchanged.
 * Pre-finalized: `__atlasFinal` so the finalizer never carves or re-gates it,
 * `__atlasGates` = the 12 gate anchors (phase 3's wall reads them).
 *
 * Coordinates are CAPITAL-LOCAL (the capital site's centre is (0,0)); a
 * district converts through `district.centre − capital.centre`.
 */
import { RoadGraphImpl } from 'enginex/city/roads/RoadGraph.js';
import { RoadGrader } from 'enginex/city/roads/RoadGrader.js';
import type { CityConfig, CityPlan, Parcel } from 'enginex/city/config.js';
import { terrainHeight } from '../../../shared/terrain.js';
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { CAPITAL_RING_ROADS, CAPITAL_WALL_R, CAPITAL_GATE_EXIT_M, capitalTheta0, boulevardAngles, serviceGateAngles, capitalMainGate, civicCoreLayout, rectCorners } from '../../../shared/capitals.js';
import { tierConfig, peekSettlementPlan } from './settlement_plan.js';
import { spaceportSpot, shuttleSpot } from './ports.js';

/** Ring-road chord length (a 30 m chord on a 490 m ring bends 3.5°). */
export const SKELETON_SEG_M = 30;
export const BOULEVARD_HALF_W = 9;
export const RING_ROAD_HALF_W = 6;
export const SERVICE_ROAD_HALF_W = 4;
/** Every gate's exit node sits this far past the wall radius (shared with the routes). */
export const GATE_EXIT_M = CAPITAL_GATE_EXIT_M;
export const SKELETON_MAX_GRADE = 0.06;
/** A district exit connects to the skeleton only within this reach (a rim node
 *  sits 30–60 m from its ring road; a boulevard passes ~55 m off a rim). */
export const CONNECT_MAX_M = 160;

interface GateAnchor { x: number; z: number; ang: number; main: boolean }
type SkeletonPlan = CityPlan & { __atlasFinal?: true; __atlasGates?: GateAnchor[] };

const TAU = Math.PI * 2;

/** The lattice slot of an angle relative to theta0 (n slots around the ring). */
function slot(a: number, th: number, n: number): number {
  const rel = (((a - th) % TAU) + TAU) % TAU;
  return Math.round(rel / (TAU / n)) % n;
}

/** Build the skeleton plan for a capital site (capital-local coordinates). */
export function capitalSkeletonPlan(site: SettlementSite, seed: string, config: CityConfig): CityPlan {
  const g = new RoadGraphImpl();
  const th = capitalTheta0(site.siteSeed);
  const blvd = boulevardAngles(th);
  const svc = serviceGateAngles(th);
  const special = new Map<string, number>(); // `${ring}:${key}` → node id at that angle
  CAPITAL_RING_ROADS.forEach((r, ri) => {
    // A multiple of 12 fill nodes so every boulevard/service angle IS a lattice angle.
    const n = 12 * Math.max(2, Math.round((TAU * r) / (SKELETON_SEG_M * 12)));
    const keyAt = new Map<number, string>();
    blvd.forEach((a, k) => keyAt.set(slot(a, th, n), `b${k}`));
    svc.forEach((a, k) => keyAt.set(slot(a, th, n), `s${k}`));
    const ids: number[] = [];
    for (let i = 0; i < n; i++) {
      const a = th + (i / n) * TAU;
      const node = g.addNode(Math.cos(a) * r, Math.sin(a) * r);
      ids.push(node.id);
      const key = keyAt.get(i);
      if (key) special.set(`${ri}:${key}`, node.id);
    }
    for (let i = 0; i < n; i++) g.addEdge(ids[i], ids[(i + 1) % n], 'collector', RING_ROAD_HALF_W);
  });
  const gates: GateAnchor[] = [];
  const last = CAPITAL_RING_ROADS.length - 1;
  // MAIN = the boulevard facing the other capital (phase 3); the ports stand outside it.
  const other = settlementSites(seed).find((o) => o.tier === 'capital' && o.id !== site.id);
  const main = other ? capitalMainGate(th, other.centerX - site.centerX, other.centerZ - site.centerZ) : 0;
  const exitOf: number[] = [];
  blvd.forEach((a, k) => {
    const chain = CAPITAL_RING_ROADS.map((_, ri) => special.get(`${ri}:b${k}`)!);
    const gate = g.addNode(Math.cos(a) * CAPITAL_WALL_R, Math.sin(a) * CAPITAL_WALL_R);
    const exit = g.addNode(Math.cos(a) * (CAPITAL_WALL_R + GATE_EXIT_M), Math.sin(a) * (CAPITAL_WALL_R + GATE_EXIT_M));
    for (let i = 0; i + 1 < chain.length; i++) g.addEdge(chain[i], chain[i + 1], 'arterial', BOULEVARD_HALF_W);
    g.addEdge(chain[last], gate.id, 'arterial', BOULEVARD_HALF_W);
    g.addEdge(gate.id, exit.id, 'arterial', BOULEVARD_HALF_W);
    exitOf[k] = exit.id;
    gates.push({ x: gate.x, z: gate.z, ang: a, main: k === main });
  });
  svc.forEach((a, k) => {
    const from = special.get(`${last}:s${k}`)!;
    const gate = g.addNode(Math.cos(a) * CAPITAL_WALL_R, Math.sin(a) * CAPITAL_WALL_R);
    const exit = g.addNode(Math.cos(a) * (CAPITAL_WALL_R + GATE_EXIT_M), Math.sin(a) * (CAPITAL_WALL_R + GATE_EXIT_M));
    g.addEdge(from, gate.id, 'collector', SERVICE_ROAD_HALF_W);
    g.addEdge(gate.id, exit.id, 'collector', SERVICE_ROAD_HALF_W);
    gates.push({ x: gate.x, z: gate.z, ang: a, main: false });
  });
  // Port spurs: local roads from the MAIN gate's exit to the spaceport and the shuttle pad (outside the wall).
  for (const spot of [spaceportSpot(seed, site), shuttleSpot(seed, site)]) {
    if (!spot) continue;
    const n = g.addNode(spot.x, spot.z);
    g.addEdge(exitOf[main], n.id, 'local', SERVICE_ROAD_HALF_W);
  }
  RoadGrader.gradeAll(g, (lx, lz) => terrainHeight(seed, site.centerX + lx, site.centerZ + lz), SKELETON_MAX_GRADE);
  // The civic core: four PARK parcels on the plaza's edges — the module's landmark
  // loop (park parcels first, cap 4) stands the assembly hall, archives, courts and
  // visitor hall there at CIV_LANDMARK_H.capital; the plaza room stays open.
  const parcels: Parcel[] = civicCoreLayout(th).parcels.map((r, i) => ({ id: i, blockId: -1, polygon: Float64Array.from(rectCorners(r)), frontageEdge: -1, zone: 'park', area: 4 * r.hw * r.hd }));
  const plan: SkeletonPlan = {
    seed: config.seed, config,
    fields: { densityAt: () => 0, zoneAt: () => 'park', tensorAt: () => ({ major: [1, 0], minor: [0, 1] }) },
    roads: g, blocks: [], parcels, buildings: [], streetProps: [],
  };
  plan.__atlasGates = gates;
  plan.__atlasFinal = true;
  return plan;
}

const skelMemo = new Map<string, CityPlan>();
/** The skeleton of a capital site — the shared plan cache first (a baked or
 *  generated capital plan IS the skeleton), else built once per process. The
 *  grader samples ~49 km of road at 2 m through the full landform terrain
 *  (~2 s cold), so the district finalizer must never rebuild it per district. */
export function capitalSkeletonFor(cap: SettlementSite, seed: string): CityPlan {
  const cached = peekSettlementPlan(seed, cap.siteSeed);
  if (cached) return cached;
  const key = `${seed}:${cap.siteSeed}`;
  let p = skelMemo.get(key);
  if (!p) { p = capitalSkeletonPlan(cap, seed, tierConfig('capital', cap.siteSeed, CAPITAL_WALL_R)); skelMemo.set(key, p); }
  return p;
}

/** Nearest point on any skeleton road (capital-local coordinates). */
export function nearestSkeletonPoint(skel: CityPlan, x: number, z: number): { x: number; z: number; d: number; edge: number } | null {
  let best: { x: number; z: number; d: number; edge: number } | null = null;
  const edges = skel.roads.edges;
  for (let ei = 0; ei < edges.length; ei++) {
    const p = edges[ei].polyline;
    for (let i = 0; i + 3 < p.length; i += 2) {
      const ax = p[i], az = p[i + 1], bx = p[i + 2], bz = p[i + 3];
      const dx = bx - ax, dz = bz - az;
      const L2 = dx * dx + dz * dz;
      const t = L2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / L2)) : 0;
      const px = ax + dx * t, pz = az + dz * t;
      const d = Math.hypot(x - px, z - pz);
      if (!best || d < best.d) best = { x: px, z: pz, d, edge: ei };
    }
  }
  return best;
}
